import * as React from "react";

import { cn } from "@/newComponents/utils/utils";

import { useUIStateStore } from "../../store/uiStateStore";

export type InputProps = {
  label?: string;
  hasError?: boolean;
  smallFont?: boolean;
  customWidth?: boolean;
} & React.InputHTMLAttributes<HTMLInputElement>;

const FormInput = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, smallFont, type, hasError, customWidth, label, ...props }, ref) => {
    const isTunecore = useUIStateStore(state => state.isTunecore);

    // Generate unique ID for standalone usage to avoid conflicts
    const inputId = React.useId();
    const standaloneId = `formTextInput-${inputId}`;

    const renderInput = (isStandalone: boolean) => {
      const inputProps = isStandalone
        ? { id: standaloneId, name: standaloneId }
        : {};

      if (isStandalone) {
        // Standalone version (with label prop)
        return (
          <input
            type={type}
            className={cn(
              "flex font-medium rounded-lg px-2 py-1.5  focus-visible:outline-none transition ease-in-out duration-200",
              customWidth ? "" : "w-full",
              smallFont ? "text-xs" : "text-sm",
              isTunecore
                ? "font-tunecore placeholder:text-white placeholder:text-opacity-50 ring-1 hover:ring-2 ring-sentric-dark-background hover:ring-sentric-orange focus-visible:ring-2 focus-visible:ring-sentric-orange bg-tunecore-light-grey"
                : "font-sentric-alt placeholder:text-black placeholder:text-opacity-85 ring-1 hover:ring-2 ring-sentric-dark-background hover:ring-sentric-orange focus-visible:ring-2 focus-visible:ring-sentric-orange",
              hasError && (isTunecore ? "ring-red-500 bg-red-100" : "ring-red-500 bg-red-100"),
              className,
            )}
            ref={ref}
            {...inputProps}
            {...props}
          />
        );
      }
      else {
        // Shadcn Form version (no label prop)
        return (
          <input
            type={type}
            className={cn(
              "flex font-medium rounded-lg px-2 py-1.5 focus-visible:outline-none transition ease-in-out duration-200",
              customWidth ? "" : "w-full",
              smallFont ? "text-xs" : "text-sm",
              isTunecore
                ? "font-tunecore bg-tunecore-light-grey placeholder:text-white placeholder:text-opacity-35 ring-1 hover:ring-2 ring-tunecore-light-grey hover:ring-tunecore-green focus-visible:ring-tunecore-green"
                : "font-sentric-alt placeholder:text-black placeholder:text-opacity-85 ring-1 hover:ring-2 ring-sentric-dark-background hover:ring-sentric-orange focus-visible:ring-2 focus-visible:ring-sentric-orange",
              hasError && (isTunecore ? "ring-red-500" : "ring-red-500 bg-red-500"),
              className,
            )}
            ref={ref}
            {...inputProps}
            {...props}
          />
        );
      }
    };

    // When used within Shadcn Form structure, don't render the wrapper div and label
    // This is detected by checking if label prop is not provided (Shadcn Form handles labels separately)
    if (label == null || label === "") {
      return renderInput(false);
    }

    // When used standalone (with label prop), render the full component with wrapper and label
    return (
      <div className={cn("flex flex-col justify-between", customWidth ? "" : "w-full")}>
        <label
          className={cn(
            "leading-snug self-start",
            smallFont ? "text-xs" : "text-base",
            isTunecore ? "font-tunecore" : "font-sentric-alt",
          )}
          htmlFor={standaloneId}
        >
          {label}
        </label>
        {renderInput(true)}
      </div>
    );
  },
);
FormInput.displayName = "FormInput";

export { FormInput };

// disabled:cursor-not-allowed disabled:opacity-50 - on editable fields release
